import type { VoicePrefs } from '@/voice/contracts';
import { parseVoicePreferences } from '@/voice/preferences';
// Settings + memory client — the UI side of src/settings.mjs / src/memory.mjs.
// Preferences and memories belong to the authenticated company account and
// are read/written through the server APIs.

// Server caps (PERSONALITY_MAX_CHARS / MAX_MEMORY_CHARS) mirrored for the UI.
import { MODEL_CHOICE_ORDER, type ModelChoice } from '@/workspace/model-choices.generated';

export const PERSONALITY_MAX_CHARS = 2000;
export const MEMORY_MAX_CHARS = 500;

/** The appearance a person chooses; `system` follows the device. */
export type ThemeChoice = 'light' | 'dark' | 'system';
export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system'];

/** Editable settings. Legacy response-mode fields are ignored on read and
 * omitted on write; the server owns the single effective mode. `theme` is
 * null until chosen on the server (and always while collaboration is off):
 * the browser then keeps its own local choice. */
export interface Settings {
  language: string | null;
  timezone: string | null;
  personality: string | null;
  chat_model_choice: ModelChoice;
  theme: ThemeChoice | null;
  voice_prefs?: VoicePrefs;
}

/** A partial write: only the keys present are changed on the server. */
export type SettingsPatch = Partial<Omit<Settings, 'voice_prefs'>> & {
  voice_prefs?: Partial<VoicePrefs>;
};

export const EMPTY_SETTINGS: Settings = {
  language: null,
  timezone: null,
  personality: null,
  chat_model_choice: 'auto',
  theme: null,
};

export const themeChoice = (value: unknown): ThemeChoice | null =>
  THEME_CHOICES.includes(value as ThemeChoice) ? (value as ThemeChoice) : null;

/** Coerce anything (server response, stored JSON) into a safe Settings. */
export function normalizeSettings(raw: unknown): Settings {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : null);
  return {
    language: str(obj.language),
    timezone: str(obj.timezone),
    personality: str(obj.personality),
    chat_model_choice: MODEL_CHOICE_ORDER.includes(obj.chat_model_choice as ModelChoice)
      ? (obj.chat_model_choice as ModelChoice)
      : 'auto',
    theme: themeChoice(obj.theme),
    ...(obj.voice_prefs
      ? { voice_prefs: parseVoicePreferences(obj.voice_prefs, { endpoint: 2000 }) ?? undefined }
      : {}),
  };
}

// ── Server settings (signed-in path) ────────────────────────────────────────

/** Null on 401 / 503 / network error. */
export async function fetchServerSettings(): Promise<Settings | null> {
  try {
    const res = await fetch('/api/v1/settings', { credentials: 'same-origin' });
    if (!res.ok) return null;
    return normalizeSettings((await res.json()).settings);
  } catch {
    return null;
  }
}

/** Writes only the keys in `patch`: a Settings-page save never sends, and so
 * never clears, the theme. */
export async function putServerSettings(patch: SettingsPatch): Promise<Settings> {
  const res = await fetch('/api/v1/settings', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return normalizeSettings((await res.json()).settings);
}

// ── Memories (signed-in only; created by the model or the account owner) ────

export interface Memory {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as {
      error?: string;
      code?: string;
      message?: string;
    } | null;
    throw new MemoryRequestError(
      res.status,
      payload?.code ?? payload?.error ?? 'memory_request_failed',
      payload?.message ?? payload?.error ?? `HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export class MemoryRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'MemoryRequestError';
  }
}

export async function listMemories(): Promise<Memory[]> {
  const { memories } = await json<{ memories: Memory[] }>(
    await fetch('/api/v1/memories', { credentials: 'same-origin' }),
  );
  return memories;
}

export async function createMemory(content: string): Promise<Memory> {
  const { memory } = await json<{ memory: Memory }>(
    await fetch('/api/v1/memories', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    }),
  );
  return memory;
}

export async function updateMemory(id: string, content: string): Promise<Memory> {
  const { memory } = await json<{ memory: Memory }>(
    await fetch(`/api/v1/memories/${id}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    }),
  );
  return memory;
}

export async function deleteMemory(id: string): Promise<void> {
  const res = await fetch(`/api/v1/memories/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) await json<never>(res);
}
